/**
 * ActivityLevelStep.js — Assessment Wizard, Screen 5 "Activity Level"
 *
 * Captures Section D of the FSD: overall activity tier, the exercise types
 * a user does, and how often (days/week, minutes/day). Used alongside
 * Screen 4's lifestyle data to compute calorie expenditure (TDEE). Built
 * on the shared AssessmentStepShell so the progress bar, step counter,
 * question header, and Previous/Next footer stay identical to every other
 * wizard screen.
 *
 * Selected-state uses the same 2.4px DIET_C.primary border (no fill) as
 * GoalStep's GoalCard and LifestyleStep's WorkTypeCard. Next is disabled
 * until the step is valid (same pattern as LifestyleStep's
 * `nextDisabled={!isValid}`).
 *
 * Frequency (Days/week, Minutes/day) only appears — and is only
 * required — once at least one exercise type is selected. A Sedentary
 * user with no structured exercise can select nothing there and still
 * advance; see ActivityLevelStep.validation.js for the exact rule.
 *
 * TDEE = BMR × activity multiplier (FSD v2.1 §3.1, DEC-02). The
 * multiplier for the selected tier is now sourced directly from
 * ACTIVITY_TIER_OPTIONS (see ActivityLevelStep.constants.js — each
 * option carries its own `multiplier`) and passed through in the onNext
 * payload as `activityMultiplier`, so the calculation engine reads the
 * number Product/Nutrition signed off on instead of re-deriving it from
 * the tier's display label.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useActivityAnalytics from "./useActivityAnalytics";
import { validateActivity } from "./ActivityLevelStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  ACTIVITY_TIER_OPTIONS,
  EXERCISE_OPTIONS,
  DAYS_PER_WEEK_MIN,
  DAYS_PER_WEEK_MAX,
  MINUTES_PER_DAY_MIN,
  MINUTES_PER_DAY_MAX,
  MINUTES_PER_DAY_STEP,
  ACTIVITY_INITIAL_DATA,
} from "./ActivityLevelStep.constants";
import {
  FieldGroup,
  FieldLabel,
  RequiredMark,
  HelperValue,
  HelperText,
  TierGrid,
  TierCard,
  TierLabel,
  TierDesc,
  ChipGrid,
  Chip,
  FrequencyRow,
  DayPillRow,
  DayPill,
  RangeInput,
  RangeScaleRow,
} from "./ActivityLevelStep.styles";

const STEP_NUMBER = STEP_NUMBERS.ACTIVITY;

const DAY_OPTIONS = Array.from(
  { length: DAYS_PER_WEEK_MAX - DAYS_PER_WEEK_MIN + 1 },
  (_, i) => DAYS_PER_WEEK_MIN + i
);

const formatMinutes = (value) =>
  value >= MINUTES_PER_DAY_MAX ? `${MINUTES_PER_DAY_MAX}+ min` : `${value} min`;

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 4 (Lifestyle).
 * @param {function} props.onNext        - Go to Screen 6, receives the full
 *   activity data object ({ activityTier, exerciseTypes, daysPerWeek,
 *   minutesPerDay }).
 */
const ActivityLevelStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useActivityAnalytics();
  const [formData, setFormData] = useState({ ...ACTIVITY_INITIAL_DATA, ...initialData });

  const { isValid, errors } = useMemo(() => validateActivity(formData), [formData]);
  const hasExercise = formData.exerciseTypes.length > 0;

  const handleTierSelect = useCallback(
    (tierId) => {
      setFormData((prev) => ({ ...prev, activityTier: tierId }));
      analytics.trackTierSelect(sessionId, tierId);
    },
    [analytics, sessionId]
  );

  const handleExerciseToggle = useCallback(
    (exerciseId) => {
      setFormData((prev) => {
        const wasSelected = prev.exerciseTypes.includes(exerciseId);
        const exerciseTypes = wasSelected
          ? prev.exerciseTypes.filter((id) => id !== exerciseId)
          : [...prev.exerciseTypes, exerciseId];
        return { ...prev, exerciseTypes };
      });
      analytics.trackExerciseToggle(
        sessionId,
        exerciseId,
        !formData.exerciseTypes.includes(exerciseId)
      );
    },
    [analytics, sessionId, formData.exerciseTypes]
  );

  const updateFrequency = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      analytics.trackFrequencyChange(sessionId, field, value);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Activity Level (Screen 5) returns to Lifestyle (Screen 4).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    // TDEE = BMR × activity multiplier (FSD v2.1 DEC-02) — attach the
    // signed-off numeric multiplier for the selected tier so the calc
    // engine never has to re-derive it from the tier id/label.
    const selectedTier = ACTIVITY_TIER_OPTIONS.find((t) => t.id === formData.activityTier);
    const payload = { ...formData, activityMultiplier: selectedTier?.multiplier ?? null };

    analytics.trackStepNext(sessionId, payload);
    // Advances the wizard to Screen 6 — the wizard's step router decides
    // which screen renders next.
    onNext?.(payload);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="Your activity level"
      description="This helps us fine-tune your daily calorie and macro targets."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel>
          Activity tier
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <TierGrid role="radiogroup" aria-label="Select your activity tier">
          {ACTIVITY_TIER_OPTIONS.map((tier) => {
            const active = tier.id === formData.activityTier;
            return (
              <TierCard
                key={tier.id}
                type="button"
                role="radio"
                aria-checked={active}
                $active={active}
                onClick={() => handleTierSelect(tier.id)}
              >
                <TierLabel>{tier.label}</TierLabel>
                <TierDesc>{tier.desc}</TierDesc>
              </TierCard>
            );
          })}
        </TierGrid>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel id="exerciseTypesLabel">
          Exercise details{" "}
          <span style={{ fontWeight: 400, color: "var(--c-text-muted)" }}>
            (optional — select all that apply)
          </span>
        </FieldLabel>
        <ChipGrid role="group" aria-labelledby="exerciseTypesLabel">
          {EXERCISE_OPTIONS.map((opt) => {
            const active = formData.exerciseTypes.includes(opt.id);
            return (
              <Chip
                key={opt.id}
                type="button"
                aria-pressed={active}
                $active={active}
                onClick={() => handleExerciseToggle(opt.id)}
              >
                <i className={`ti ${opt.icon}`} aria-hidden="true" />
                {opt.label}
              </Chip>
            );
          })}
        </ChipGrid>
      </FieldGroup>

      {hasExercise ? (
        <FrequencyRow>
          <FieldGroup>
            <FieldLabel id="daysPerWeekLabel">
              Days/week
              <RequiredMark>*</RequiredMark> — <HelperValue>{formData.daysPerWeek}</HelperValue>
            </FieldLabel>
            <DayPillRow role="radiogroup" aria-labelledby="daysPerWeekLabel">
              {DAY_OPTIONS.map((day) => {
                const active = formData.daysPerWeek === day;
                return (
                  <DayPill
                    key={day}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    $active={active}
                    onClick={() => updateFrequency("daysPerWeek", day)}
                  >
                    {day}
                  </DayPill>
                );
              })}
            </DayPillRow>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="minutesPerDay">
              Minutes/day
              <RequiredMark>*</RequiredMark> —{" "}
              <HelperValue>{formatMinutes(formData.minutesPerDay)}</HelperValue>
            </FieldLabel>
            <RangeInput
              id="minutesPerDay"
              min={MINUTES_PER_DAY_MIN}
              max={MINUTES_PER_DAY_MAX}
              step={MINUTES_PER_DAY_STEP}
              value={formData.minutesPerDay}
              onChange={(e) => updateFrequency("minutesPerDay", Number(e.target.value))}
            />
            <RangeScaleRow>
              <span>0 min</span>
              <span>{MINUTES_PER_DAY_MAX}+ min</span>
            </RangeScaleRow>
          </FieldGroup>
        </FrequencyRow>
      ) : (
        <HelperText>
          Not doing any structured exercise right now? That's fine — select at least one
          activity above if you'd like us to factor its frequency into your plan.
        </HelperText>
      )}
    </AssessmentStepShell>
  );
};

ActivityLevelStep.displayName = "ActivityLevelStep";
export default React.memo(ActivityLevelStep);